import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import {
  CancelOutlined as RejectIcon,
  CheckCircleOutlined as ApproveIcon,
  DrawOutlined as SignatureIcon,
  FactoryOutlined as FacilityIcon,
  ImageNotSupportedOutlined as NoPhotoIcon,
  LocalShippingOutlined as TruckIcon,
  PersonOutlined as PersonIcon,
  PlaceOutlined as PlaceIcon,
  PrintOutlined as PrintIcon,
  ReceiptLongOutlined as InvoiceIcon,
  ScaleOutlined as WeightIcon,
  ScheduleOutlined as TimeIcon,
  StorefrontOutlined as SupplierIcon,
} from '@mui/icons-material';
import { useReactToPrint } from 'react-to-print';
import PageHeader from '../../components/PageHeader';
import { Detail, dedupeByAssetId } from '../../components/monitoring/EvidenceSections';
import ApproveOrderDialog, { type ApproveOrderTarget } from '../../components/orders/ApproveOrderDialog';
import RejectOrderDialog, { type RejectOrderTarget } from '../../components/orders/RejectOrderDialog';
import PhotoLightbox from '../../components/orders/PhotoLightbox';
import { APP_SHAPE } from '../../constants/shape';
import { useAuthPermissions } from '../../hooks/useAuthPermissions';
import { getDeliveryByPickupOrder } from '../../services/delivery.service';
import { useOrganizationStore } from '../../stores/organization.store';
import type { DeliveryDetails, DeliveryEvidenceFile } from '../../types/delivery.type';
import { formatKg, formatSar, formatWhen } from '../../utils/formatters';
import { handoffRoleFor, outcomeColor, outcomeFromApproval } from '../../utils/handoffView';

const MISSING_PRICE_FLAG = 'missing_agreement_price';

type TitledPhoto = DeliveryEvidenceFile & { title: string };

export default function OrderDetails() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { pickupOrderId } = useParams<{ pickupOrderId: string }>();
  const selectedOrganization = useOrganizationStore((state) => state.selectedOrganization);
  const { canMutate } = useAuthPermissions();
  const [details, setDetails] = useState<DeliveryDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<'general' | 'evidence'>('general');
  const [approveTarget, setApproveTarget] = useState<ApproveOrderTarget | null>(null);
  const [rejectTarget, setRejectTarget] = useState<RejectOrderTarget | null>(null);
  const [lightbox, setLightbox] = useState<TitledPhoto | null>(null);
  const receiptRef = useRef<HTMLDivElement>(null);
  const printReceipt = useReactToPrint({ contentRef: receiptRef });

  const orgId = selectedOrganization?.id;
  const canApprove = Boolean(orgId && canMutate(`recyclee.${orgId}.*.order_approval.approve`));
  const locale = i18n.resolvedLanguage || i18n.language;

  const load = useCallback(() => {
    if (!pickupOrderId) return;
    setLoading(true);
    setError(null);
    getDeliveryByPickupOrder(pickupOrderId)
      .then(setDetails)
      .catch((requestError) =>
        setError(requestError?.response?.data?.detail ?? t('monitoring.evidence.loadError')))
      .finally(() => setLoading(false));
  }, [pickupOrderId, t]);

  useEffect(() => { load(); }, [load]);

  const photos = useMemo<TitledPhoto[]>(() => {
    if (!details) return [];
    const titled = (files: DeliveryEvidenceFile[] | undefined, title: string) =>
      dedupeByAssetId(files ?? []).map((file) => ({ ...file, title }));
    const events = details.events;
    const flowPhotos: DeliveryEvidenceFile[] = events.flatMap((event) => {
      const answers = (event.enriched_data as any)?.flow?.answers ?? {};
      return Object.values(answers as Record<string, any>).flatMap((answer: any) =>
        [...(answer?.files ?? []), ...(answer?.file ? [answer.file] : [])].filter((f) => f?.asset_id),
      );
    });
    return [
      ...titled(events.flatMap((e) => e.enriched_data.before_photos ?? []), t('monitoring.evidence.beforePhotos')),
      ...titled(events.flatMap((e) => e.enriched_data.after_photos ?? []), t('monitoring.evidence.afterPhotos')),
      ...titled(events.flatMap((e) => e.enriched_data.recycler_photos ?? []), t('monitoring.evidence.recyclerPhotos')),
      ...titled(flowPhotos, t('orderDetails.flowPhotos')),
      ...(details.weight_invoice?.photo ? titled([details.weight_invoice.photo], t('monitoring.evidence.weightInvoice')) : []),
    ];
  }, [details, t]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  if (error || !details) {
    return <Alert severity="error" action={<Button color="inherit" onClick={load}>{t('suppliers.details.retry')}</Button>}>{error ?? t('monitoring.evidence.loadError')}</Alert>;
  }

  const order = details.pickup_order;
  const orderData = (order.data ?? {}) as Record<string, any>;
  const approval = details.pickup_approval;
  const invoice = details.weight_invoice;
  const flags: string[] = orderData.flags ?? [];
  const missingPrice = flags.includes(MISSING_PRICE_FLAG);
  const isApproved = order.approval_status === 'approved';
  const isPending = order.approval_status === 'pending';
  const isRejected = order.approval_status === 'rejected';
  const confirmed = approval?.status === 'approved';
  // Which side of a handed-off order we are on decides what this page may show: the
  // deliverer never sees the receiver's settlement, the receiver never sees the source.
  const role = handoffRoleFor(details.handoff, orgId);
  const isOrigin = role === 'origin';
  const isDestination = role === 'destination';
  const outcome = details.handoff?.outcome ?? outcomeFromApproval(order.approval_status);
  const weightKg: number | null = isOrigin
    ? (details.delivery.estimated_weight ?? null)
    : (orderData.verified_weight_kg ?? null);
  const pointName = isDestination
    ? t('orderDetails.handoff.viaOrg', { org: details.handoff?.from_org_name ?? '—' })
    : (order.collection_point_name ?? orderData.pickup_address ?? '—');
  const supplierName = order.supplier_name ?? details.waste_outlet?.name ?? '—';
  const canDecide = canApprove && isPending && !isOrigin;

  const openApprove = () =>
    setApproveTarget({
      pickupOrderId: order.id,
      pointName,
      supplierName,
      weightKg,
      confirmation: approval ? { status: approval.status, name: approval.approver_name, respondedAt: approval.responded_at } : null,
    });

  const openReject = () =>
    setRejectTarget({ pickupOrderId: order.id, pointName, supplierName, weightKg });

  const statusChip = (
    <Chip
      size="small"
      color={outcomeColor(outcome)}
      label={
        isApproved
          ? t('orderDetails.approvalStatus.approved')
          : isPending
            ? t('collectionsMonitor.approvalStatus.pending')
            : isRejected
              ? t('collectionsMonitor.approvalStatus.rejected')
              : order.status
      }
    />
  );

  return (
    <Box>
      <PageHeader
        title={t('orderDetails.pageTitle')}
        subtitle={<Stack direction="row" spacing={1} alignItems="center"><span>#{order.id}</span>{statusChip}</Stack>}
        onBack={() => navigate(-1)}
        backLabel={t('transportation.routes.details.backLabel')}
        sx={{ borderRadius: `${APP_SHAPE.radiusLg}px` }}
        actions={(
          <Stack direction="row" spacing={1}>
            {canDecide ? (
              <>
                <Button variant="contained" color="success" startIcon={<ApproveIcon />} onClick={openApprove}>
                  {t('orderDetails.approve')}
                </Button>
                <Button variant="outlined" color="error" startIcon={<RejectIcon />} onClick={openReject}>
                  {t('orderDetails.reject')}
                </Button>
              </>
            ) : null}
            {!isOrigin ? (
              <Button
                variant="outlined"
                startIcon={<PrintIcon />}
                disabled={!isApproved}
                onClick={() => printReceipt()}
                title={isApproved ? undefined : t('orderDetails.exportNeedsApproval')}
              >
                {t('orderDetails.exportReceipt')}
              </Button>
            ) : null}
          </Stack>
        )}
      />

      {isPending && !isOrigin ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {missingPrice ? t('orderDetails.pendingMissingPrice') : t('orderDetails.pendingApproval')}
        </Alert>
      ) : null}
      {isRejected ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight={600}>
            {t('orderDetails.rejectedTitle', { when: formatWhen(order.rejected_at, locale) })}
          </Typography>
          <Typography variant="body2">{order.rejection_reason ?? details.handoff?.rejection_reason ?? '—'}</Typography>
        </Alert>
      ) : null}
      {isOrigin && details.handoff ? (
        <Paper variant="outlined" sx={{ p: 2.5, mb: 2, borderRadius: `${APP_SHAPE.radiusLg}px` }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
            <FacilityIcon fontSize="small" color="action" />
            <Typography variant="subtitle1" fontWeight={600}>{t('orderDetails.handoff.title')}</Typography>
            <Chip size="small" color={outcomeColor(outcome)} label={t(`orderDetails.handoff.outcome.${outcome ?? 'pending'}`)} />
          </Stack>
          <Typography variant="body2">
            {t('orderDetails.handoff.deliveredTo', {
              facility: details.handoff.destination_facility_name ?? '—',
              org: details.handoff.destination_org_name ?? '—',
              when: formatWhen(details.handoff.delivered_at ?? details.delivery.delivery_at, locale),
            })}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
            {t('orderDetails.handoff.originHint')}
          </Typography>
        </Paper>
      ) : null}

      <Paper elevation={1} sx={{ borderRadius: `${APP_SHAPE.radiusLg}px`, overflow: 'hidden' }}>
        <Tabs value={tab} onChange={(_, next) => setTab(next)} sx={{ px: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Tab value="general" label={t('orderDetails.tabGeneral')} />
          <Tab value="evidence" label={`${t('orderDetails.tabEvidence')} (${photos.length})`} />
        </Tabs>

        {tab === 'general' ? (
          <Box sx={{ p: { xs: 2, sm: 3 } }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={7}>
                <Stack spacing={2}>
                  <Paper variant="outlined" sx={{ p: 2.5, borderRadius: `${APP_SHAPE.radiusLg}px` }}>
                    <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>{t('orderDetails.mainDetails')}</Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2.5 }}>
                      <Detail icon={<InvoiceIcon />} label={t('orderDetails.wasteType')}>{order.material_type?.name ?? order.waste_type ?? '—'}</Detail>
                      <Detail icon={<WeightIcon />} label={isOrigin ? t('orderDetails.collectedWeight') : t('orderDetails.weight')}>
                        {formatKg(weightKg, locale)}
                        {!isOrigin && orderData.weight_corrected_by_user_id ? <Chip size="small" label={t('orderDetails.corrected')} sx={{ ml: 1 }} /> : null}
                      </Detail>
                      {!isOrigin ? (
                        <>
                          <Detail icon={<InvoiceIcon />} label={t('orderDetails.price')}>
                            {formatSar(order.sale_amount, locale)}
                            {missingPrice ? <Chip size="small" color="warning" label={t('orderDetails.missingPrice')} sx={{ ml: 1 }} /> : null}
                          </Detail>
                          <Detail icon={<InvoiceIcon />} label={t('monitoring.evidence.serialNumber')}>{invoice?.serial_number ?? '—'}</Detail>
                        </>
                      ) : null}
                      <Detail icon={<PersonIcon />} label={t('orderDetails.driver')}>{details.driver?.username ?? details.driver?.phone ?? '—'}</Detail>
                      <Detail icon={<TruckIcon />} label={t('orderDetails.truck')}>{details.truck?.name ?? '—'}</Detail>
                      <Detail icon={<TimeIcon />} label={t('orderDetails.completedAt')}>{formatWhen(details.delivery.delivery_at, locale)}</Detail>
                      {!isOrigin ? (
                        <Detail icon={<ApproveIcon />} label={t('orderDetails.approvedAt')}>{formatWhen(order.approved_at, locale)}</Detail>
                      ) : null}
                    </Box>
                  </Paper>

                  <Paper variant="outlined" sx={{ p: 2.5, borderRadius: `${APP_SHAPE.radiusLg}px` }}>
                    <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                      {isDestination ? t('orderDetails.handoff.deliveredBy') : t('orderDetails.pointAndSupplier')}
                    </Typography>
                    {isDestination ? (
                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2.5 }}>
                        <Detail icon={<SupplierIcon />} label={t('orderDetails.handoff.deliveringOrg')}>{details.handoff?.from_org_name ?? supplierName}</Detail>
                        <Detail icon={<FacilityIcon />} label={t('orderDetails.handoff.facility')}>{details.handoff?.destination_facility_name ?? '—'}</Detail>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2.5 }}>
                        <Detail icon={<PlaceIcon />} label={t('orderDetails.point')}>
                          {pointName}
                          {orderData.pickup_address && order.collection_point_name ? (
                            <Typography variant="caption" color="text.secondary" display="block">{orderData.pickup_address}</Typography>
                          ) : null}
                        </Detail>
                        <Detail icon={<SupplierIcon />} label={t('suppliers.collectionPoints.table.supplier')}>{supplierName}</Detail>
                      </Box>
                    )}
                  </Paper>

                  {!isDestination ? (
                  <Paper variant="outlined" sx={{ p: 2.5, borderRadius: `${APP_SHAPE.radiusLg}px` }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                      <SignatureIcon fontSize="small" color={confirmed ? 'success' : 'disabled'} />
                      <Typography variant="subtitle1" fontWeight={600}>{t('orderDetails.pickupApproval')}</Typography>
                      {approval ? (
                        <Chip size="small" color={confirmed ? 'success' : approval.status === 'rejected' ? 'error' : 'default'} label={t(`orderDetails.approvalStatus.${approval.status}`)} />
                      ) : null}
                    </Stack>
                    {confirmed ? (
                      <Box>
                        <Typography variant="body2">
                          {t('orderDetails.confirmedBy')} <strong>{approval?.approver_name}</strong> · {formatKg(approval?.weight_kg, locale)} · {formatWhen(approval?.responded_at, locale)}
                        </Typography>
                        {approval?.signature_base64 ? (
                          <Box component="img" src={approval.signature_base64} alt="signature" sx={{ mt: 1, maxWidth: 260, maxHeight: 120, border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: '#fff' }} />
                        ) : null}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {approval?.rejection_note ? `${t('orderDetails.approvalStatus.rejected')}: ${approval.rejection_note}` : t('orderDetails.notConfirmed')}
                      </Typography>
                    )}
                  </Paper>
                  ) : null}
                </Stack>
              </Grid>

              <Grid item xs={12} md={5}>
                <Paper variant="outlined" sx={{ p: 2.5, borderRadius: `${APP_SHAPE.radiusLg}px`, height: '100%' }}>
                  <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>{t('orderDetails.timeline')}</Typography>
                  <Stack spacing={1.5}>
                    {details.events.map((event) => (
                      <Stack key={event.id} direction="row" spacing={1.25} alignItems="flex-start">
                        <TimeIcon fontSize="small" sx={{ color: 'text.secondary', mt: 0.25 }} />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={600} sx={{ textTransform: 'capitalize' }}>{event.phase.replace(/_/g, ' ')}</Typography>
                          <Typography variant="caption" color="text.secondary">{formatWhen(event.event_at, locale)}</Typography>
                        </Box>
                      </Stack>
                    ))}
                  </Stack>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        ) : (
          <Box sx={{ p: { xs: 2, sm: 3 } }}>
            {photos.length === 0 ? (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ color: 'text.secondary', py: 4, justifyContent: 'center' }}>
                <NoPhotoIcon fontSize="small" />
                <Typography variant="body2">{t('monitoring.evidence.noPhotos')}</Typography>
              </Stack>
            ) : (
              <ImageList cols={4} gap={12} sx={{ m: 0 }}>
                {photos.map((photo) => (
                  <ImageListItem key={`${photo.title}-${photo.asset_id}`} sx={{ cursor: 'zoom-in', borderRadius: 1, overflow: 'hidden' }} onClick={() => setLightbox(photo)}>
                    <Box component="img" src={photo.download_url ?? undefined} alt={photo.title} loading="lazy" sx={{ width: '100%', aspectRatio: '1 / 1', objectFit: 'cover', display: 'block' }} />
                    <ImageListItemBar title={photo.title} subtitle={photo.filename ?? undefined} />
                  </ImageListItem>
                ))}
              </ImageList>
            )}
          </Box>
        )}
      </Paper>

      <PhotoLightbox photo={lightbox} onClose={() => setLightbox(null)} />
      {orgId ? (
        <>
          <ApproveOrderDialog open={Boolean(approveTarget)} organizationId={orgId} target={approveTarget} onClose={() => setApproveTarget(null)} onApproved={load} />
          <RejectOrderDialog open={Boolean(rejectTarget)} organizationId={orgId} target={rejectTarget} onClose={() => setRejectTarget(null)} onRejected={load} />
        </>
      ) : null}

      {/* Printable receipt: off-screen; react-to-print lifts it into the print window. */}
      <Box sx={{ display: 'none' }}>
        <Box ref={receiptRef} sx={{ p: 4, fontFamily: 'Arial, sans-serif', color: '#000' }}>
          <Typography variant="h5" sx={{ mb: 0.5 }}>{t('orderDetails.receiptTitle')}</Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>#{order.id}</Typography>
          <Divider sx={{ mb: 2 }} />
          <table style={{ width: '100%', fontSize: 14, borderCollapse: 'collapse' }}>
            <tbody>
              {[
                [t('orderDetails.point'), pointName],
                [t('suppliers.collectionPoints.table.supplier'), supplierName],
                [t('orderDetails.wasteType'), order.material_type?.name ?? order.waste_type ?? '—'],
                [t('orderDetails.driver'), details.driver?.username ?? '—'],
                [t('orderDetails.truck'), details.truck?.name ?? '—'],
                [t('orderDetails.weight'), formatKg(weightKg, locale)],
                [t('orderDetails.price'), formatSar(order.sale_amount, locale)],
                [t('monitoring.evidence.serialNumber'), invoice?.serial_number ?? '—'],
                [t('orderDetails.completedAt'), formatWhen(details.delivery.delivery_at, locale)],
                [t('orderDetails.approvedAt'), formatWhen(order.approved_at, locale)],
              ].map(([label, value]) => (
                <tr key={String(label)}>
                  <td style={{ padding: '6px 8px', border: '1px solid #ccc', fontWeight: 600, width: '40%' }}>{label}</td>
                  <td style={{ padding: '6px 8px', border: '1px solid #ccc' }}>{value as string}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {confirmed ? (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2">{t('orderDetails.pickupApproval')}</Typography>
              <Typography variant="body2">{approval?.approver_name} · {formatKg(approval?.weight_kg, locale)} · {formatWhen(approval?.responded_at, locale)}</Typography>
              {approval?.signature_base64 ? <img src={approval.signature_base64} alt="signature" style={{ maxHeight: 90, marginTop: 6 }} /> : null}
            </Box>
          ) : null}
          <Box sx={{ mt: 2, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {photos.slice(0, 4).map((photo) => (
              <img key={photo.asset_id} src={photo.download_url ?? undefined} alt={photo.title} style={{ width: 140, height: 140, objectFit: 'cover' }} />
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
